import { useEffect, useRef, useState } from "react";
import {
  CalendarClock,
  Car,
  ChartBar,
  Home,
  Inbox,
  ListTodo,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Target,
  UserRound,
} from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { AppMe } from "@lumika/shared";
import {
  BottomNav,
  BottomNavLabel,
  Button,
  MobileAppBar,
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
  SkipLink,
  TickerProvider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@lumika/ui";
import { DealershipSwitcher } from "../components/DealershipSwitcher";
import { GlobalSearch } from "../components/GlobalSearch";
import { HotkeysHelp } from "../components/HotkeysHelp";
import { LumikaLogo } from "@lumika/ui";
import { NavList, type NavGroup, type NavItem } from "../components/NavList";
import { NewMenu } from "../components/NewMenu";
import { NotificationBell } from "../components/NotificationBell";
import { UserMenu } from "../components/UserMenu";
import { useAppBadge } from "../hooks/useAppBadge";
import { useHotkeys } from "../lib/hotkeys";
import { canManageSetup } from "../lib/access";
import { RealtimeContext, useRealtime } from "../realtime/useRealtime";

// Desktop sidebar collapse state, persisted so an operator's preferred
// chrome width survives reloads.
const NAV_COLLAPSED_KEY = "lumika.nav.collapsed";

function loadNavCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function saveNavCollapsed(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    // localStorage may be unavailable; collapse still works for the session.
  }
}

// MembershipRoleSchema vocabulary, most privileged first.
const ROLE_ORDER = [
  "dealership_owner",
  "dealership_admin",
  "sales_manager",
  "service_manager",
  "bdc_agent",
  "sales_agent",
  "service_agent",
  "readonly",
];

function roleRank(role: string): number {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
}

export function primaryRoleForMemberships(
  memberships: readonly { role: string }[],
): string {
  return (
    memberships
      .map((membership) => membership.role)
      .sort((a, b) => roleRank(a) - roleRank(b))[0] ?? "readonly"
  );
}

const OPERATIONAL_ROLES = new Set([
  "bdc_agent",
  "sales_agent",
  "service_agent",
]);

export function defaultLandingForRole(role: string): string {
  return OPERATIONAL_ROLES.has(role) ? "/inbox?view=needs_you" : "/home";
}

export function mobilePrimaryRoutesForRole(role: string): readonly string[] {
  if (OPERATIONAL_ROLES.has(role)) {
    return ["/inbox", "/leads", "/tasks", "/calendar"];
  }
  if (role === "readonly") {
    return ["/home", "/leads", "/contacts", "/insights"];
  }
  return ["/home", "/inbox", "/leads", "/insights"];
}

export function AppShell({ me }: { me: AppMe }) {
  const showSetup = canManageSetup(me.memberships);
  const role = primaryRoleForMemberships(me.memberships);
  const overviewLabel = OPERATIONAL_ROLES.has(role) ? "Home" : "Overview";
  const hasMultipleDealerships = me.accessibleDealerships.length > 1;
  const dealershipInitials = me.dealership.name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(loadNavCollapsed);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      saveNavCollapsed(next);
      return next;
    });
  }

  // "[" toggles the desktop rail the way code editors toggle their sidebar.
  // The registry already swallows single keys while typing and under open
  // overlays (hotkeys.ts), so this stays inert in inputs and dialogs.
  useHotkeys([
    {
      keys: "[",
      scope: "Global",
      description: "Collapse or expand the sidebar",
      onTrigger: () => toggleCollapsed(),
    },
  ]);

  // Server-pushed cache invalidations + presence (UIUX §5.3).
  const realtime = useRealtime(me.user.id);

  // Title-bar "(3) Lumika" + PWA icon badge; needs-you count feeds the
  // bottom-nav Inbox badge (UIUX §5.5 / §8.1).
  const { needsYou } = useAppBadge();

  // Full-screen mobile details (thread, contact 360) own the bottom
  // edge: the global BottomNav + compose FAB would cover the composer
  // and eat 3.5rem of thumb zone (UIUX §8.1).
  const fullScreenDetail =
    /^\/inbox\/[^/]+/.test(location.pathname) ||
    /^\/contacts\/[^/]+/.test(location.pathname);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    function onChange(e: MediaQueryListEvent) {
      if (e.matches) setDrawerOpen(false);
    }
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  const allItems: NavItem[] = [
    {
      to: "/home",
      label: overviewLabel,
      icon: <Home className="h-5 w-5" />,
      group: "home",
      hint: "Dashboard — what needs you now",
    },
    {
      to: "/inbox",
      label: "Inbox",
      icon: <Inbox className="h-5 w-5" />,
      group: "work",
      hint: "Leads that need a reply now",
    },
    {
      to: "/leads",
      label: "Leads",
      icon: <Target className="h-5 w-5" />,
      group: "work",
      hint: "Every deal in your pipeline — a person can have several",
    },
    {
      to: "/contacts",
      label: "Contacts",
      icon: <UserRound className="h-5 w-5" />,
      group: "work",
      hint: "People. Open one to see all their leads and history",
    },
    {
      to: "/calendar",
      label: "Calendar",
      icon: <CalendarClock className="h-5 w-5" />,
      group: "work",
    },
    {
      to: "/tasks",
      label: "Tasks",
      icon: <ListTodo className="h-5 w-5" />,
      group: "work",
    },
    {
      to: "/inventory",
      label: "Inventory",
      icon: <Car className="h-5 w-5" />,
      group: "business",
    },
    {
      to: "/insights",
      label: "Reports",
      icon: <ChartBar className="h-5 w-5" />,
      group: "business",
      hint: "Speed-to-lead, conversion, appointments",
    },
    ...(showSetup
      ? [
          {
            to: "/settings",
            label: "Settings",
            icon: <Settings className="h-5 w-5" />,
            group: "settings" as NavGroup,
          },
        ]
      : []),
  ];
  // Overview and Reports are manager/read-only decision surfaces. Keep the
  // desktop rail otherwise stable, but do not make operational agents step
  // past two manager destinations on every trip to Tasks or Calendar.
  const items = OPERATIONAL_ROLES.has(role)
    ? allItems.filter((item) => item.to !== "/home" && item.to !== "/insights")
    : allItems;

  const primaryRoutes = mobilePrimaryRoutesForRole(role);
  const primaryItems = primaryRoutes
    .map((route) => items.find((item) => item.to === route))
    .filter((item): item is NavItem => Boolean(item));
  const activeItem = items.find((item) => location.pathname.startsWith(item.to));

  return (
    // TickerProvider: one shared 1Hz interval for every live timer
    // (SlaTimer / BandChip) rendered anywhere in the shell — one
    // interval per page instead of one per row (UIUX §3.2).
    <TickerProvider>
    <RealtimeContext.Provider value={realtime}>
    <div
      className={cn(
        "flex h-dvh flex-col overflow-hidden bg-background text-foreground",
        "md:grid md:h-screen md:grid-rows-[auto_1fr]",
        "md:transition-[grid-template-columns] md:duration-200 md:ease-out motion-reduce:transition-none",
        collapsed
          ? "md:grid-cols-[4rem_minmax(0,1fr)]"
          : "md:grid-cols-[240px_minmax(0,1fr)]",
      )}
    >
      <SkipLink />
      {/* Mobile top bar — hidden at md+ */}
      <MobileAppBar
        leading={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        }
        title={activeItem?.label ?? "Lumika"}
        subtitle={me.dealership.name}
        trailing={
          <>
            <GlobalSearch variant="mobile" dealershipId={me.dealership.id} />
            <NotificationBell />
          </>
        }
      />

      {/* Desktop sidebar (≥md) */}
      <aside className="hidden h-full min-h-0 flex-col overflow-hidden border-r bg-card md:flex md:row-span-2">
        <TooltipProvider delayDuration={300}>
          {/* h-12 header band — its border-b continues the topbar's h-12
              seam straight across the shell instead of dead-ending. */}
          <header
            className={cn(
              "flex h-12 shrink-0 items-center border-b",
              collapsed ? "justify-center px-0" : "gap-2.5 px-4",
            )}
          >
            <LumikaLogo theme="auto" size={28} />
            {!collapsed && (
              <>
                <span className="flex-1 truncate text-sm font-semibold tracking-tight text-foreground">
                  lumika
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleCollapsed}
                      aria-label="Collapse navigation"
                      className="h-8 w-8 shrink-0 text-muted-foreground sm:h-8 sm:w-8"
                    >
                      <PanelLeftClose />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Collapse</TooltipContent>
                </Tooltip>
              </>
            )}
          </header>

          {/* Collapsed rail: the collapse toggle moves to a centered button
              under the header — there's no room for it beside the mark. */}
          {collapsed && (
            <div className="flex justify-center py-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleCollapsed}
                    aria-label="Expand navigation"
                    className="h-8 w-8 text-muted-foreground sm:h-8 sm:w-8"
                  >
                    <PanelLeftOpen />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Expand</TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Dealership context. When collapsed the initials chip is now a
              plain label — it no longer secretly toggles the rail. */}
          {!collapsed ? (
            <div className="px-4 pb-3 pt-3">
              <DealershipSwitcher
                accessibleDealerships={me.accessibleDealerships}
                currentName={me.dealership.name}
              />
            </div>
          ) : hasMultipleDealerships ? (
            <div className="flex justify-center px-2 pb-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label={`Current dealership: ${me.dealership.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-[11px] font-bold text-accent-foreground"
                  >
                    {dealershipInitials}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right">{me.dealership.name}</TooltipContent>
              </Tooltip>
            </div>
          ) : null}

          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto [scrollbar-width:thin] px-2 pt-1">
            <NavList items={items} collapsed={collapsed} />
          </nav>

          <footer className={cn("border-t p-2", collapsed && "flex justify-center")}>
            <UserMenu
              variant={collapsed ? "compact" : "sidebar"}
              user={me.user}
              role={role}
            />
          </footer>
        </TooltipProvider>
      </aside>

      {/* Desktop topbar — row 1 col 2, hidden on mobile. Controls sit on a
          single h-8 baseline; identity now lives only in the sidebar menu. */}
      <header className="hidden md:flex h-12 shrink-0 items-center gap-2 border-b bg-card px-4">
        <GlobalSearch dealershipId={me.dealership.id} />
        <div className="flex-1" />
        <NewMenu />
        <NotificationBell />
      </header>

      {/* Main content — row 2 col 2 on desktop; fills remaining space on mobile */}
      <main
        id="main-content"
        tabIndex={-1}
        ref={mainRef}
        className={cn(
          "flex min-w-0 flex-1 flex-col overflow-y-auto md:pb-0 md:min-h-0",
          fullScreenDetail ? "pb-0" : "pb-bottom-nav-safe",
        )}
      >
        <Outlet />
      </main>

      {/* Mobile bottom navigation — hidden while a full-screen detail
          (thread / contact 360) owns the bottom edge (UIUX §8.1). */}
      {!fullScreenDetail && (
      <BottomNav>
        {primaryItems.map((item) => (
          <li key={item.to} className="contents">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "relative flex h-full min-w-touch items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  isActive && "before:absolute before:inset-x-6 before:top-0 before:h-0.5 before:bg-primary",
                )
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  <BottomNavLabel active={isActive} icon={item.icon} label={item.label} />
                  {item.to === "/inbox" && needsYou > 0 && (
                    // Real needs-you count from GET /inbox/counts (B3),
                    // not a client-side page count (UIUX §8.1).
                    <span
                      aria-label={`${needsYou} conversations need you`}
                      className="absolute -right-2 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
                    >
                      {needsYou > 99 ? "99+" : needsYou}
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          </li>
        ))}
        <li className="contents">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
            aria-label="More navigation"
            className="relative h-full min-w-touch rounded-none px-0 focus-visible:ring-inset"
          >
            <BottomNavLabel active={false} icon={<Menu className="h-5 w-5" />} label="More" />
          </Button>
        </li>
      </BottomNav>
      )}

      {/* Mobile FAB — above bottom-nav; hidden with it on full-screen
          details so it never sits over the composer (UIUX §8.1). */}
      {/* Mobile nav drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="md:hidden">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {/* TooltipProvider so NavList's hint tooltips have an ancestor
              here too — the rail has its own; the drawer needs its own. */}
          <TooltipProvider delayDuration={300}>
            <header className="flex flex-col gap-3 px-5 pt-3">
              <div className="flex items-center gap-2.5">
                <LumikaLogo theme="auto" size={28} />
                <span className="flex-1 truncate text-sm font-semibold tracking-tight text-foreground">
                  lumika
                </span>
              </div>
              <DealershipSwitcher
                accessibleDealerships={me.accessibleDealerships}
                currentName={me.dealership.name}
              />
            </header>
            <SheetBody className="px-2">
              <nav className="flex flex-col gap-0.5">
                <NavList items={items} onNavigate={() => setDrawerOpen(false)} />
              </nav>
            </SheetBody>
            <footer className="border-t bg-background p-2">
              <UserMenu variant="sidebar" user={me.user} role={role} />
            </footer>
          </TooltipProvider>
        </SheetContent>
      </Sheet>
      {/* Keyboard dispatcher + `?` cheat-sheet (mounted once, app-wide). */}
      <HotkeysHelp />
    </div>
    </RealtimeContext.Provider>
    </TickerProvider>
  );
}
